import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { TooltipTrigger } from "@radix-ui/react-tooltip"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { LoaderCircleIcon, PlusIcon, Trash2Icon, TrashIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData } from "react-router"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type {
  DocumentEvidenceCategory,
  Lens,
  EvidenceTheme,
  DocumentTheme,
  DocumentClassificationType,
  Advisories,
} from "@/utils/types"
import { ThemeUsageLabel, UsageOptions } from "@/components/ThemeUsage"
import { type FieldDetail, FieldInfoComponent } from "@/components/FieldInfo"

const BRAND_DOCUMENT_CLASSIFICATION_EVIDENCE_THEME_FIELDS = {
  USAGE: "usage",
  THEME: "theme",
  CLASSIFICATION: "classification",
  LENS: "lens",
} as const

const routeId = "brand-document-evidence-themes"
const sessionKey = "brand-document-evidence-themes-filter-storage"

async function loader() {
  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]
  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedLens = filters.parsed?.lens
  const loadedGroup = filters.parsed?.group
  const loadedClassification = filters.parsed?.classification

  const loadedProject =
    filters.parsed?.project == null
      ? projects[0]
      : (projects.find((prj) => prj === filters.parsed?.project) ?? projects[0])

  const [classificationToThemeMapping, documentClassifications, lenses, evidenceThemes, fieldInfoArray] =
    await Promise.all([
      fetchAPI<DocumentEvidenceCategory[]>(
        `/api/UnityViewer/DocumentClassificationEvidenceTheme/${loadedOrganisation}/${loadedProject}${qs.stringify({ lensCode: loadedLens, classification: loadedClassification }, { addQueryPrefix: true, skipNulls: true })}`,
      )
        .then(handleResponse)
        .catch(() => []),
      fetchAPI<DocumentClassificationType[]>(
        `/api/UnityViewer/DocumentClassification/${loadedOrganisation}/${loadedProject}`,
      )
        .then(handleResponse)
        .catch(() => []),
      fetchAPI<Lens[]>(`/api/UnityViewer/lens/${loadedOrganisation}/${loadedProject}`)
        .then(handleResponse)
        .catch(() => []),
      fetchAPI<EvidenceTheme[]>(`/api/UnityViewer/EvidenceTheme/${loadedOrganisation}/${loadedProject}`)
        .then(handleResponse)
        .catch(() => []),
      fetchAPI<FieldDetail[]>(`/api/sections/Unity Viewer/Brand Document Classification Evidence Themes`)
        .then(handleResponse)
        .catch(() => []),
    ])

  const groupedEvidenceThemes = evidenceThemes.reduce<Map<string, Omit<EvidenceTheme, "usage"> & { usage: string[] }>>(
    (acc, cur) => {
      if (acc.has(cur.name)) acc.get(cur.name)?.usage.push(cur.usage)
      else acc.set(cur.name, { ...cur, usage: [cur.usage] })

      return acc
    },
    new Map(),
  )

  return {
    classificationToThemeMapping,
    documentClassifications,
    loadedOrganisation,
    loadedProject,
    projects,
    loadedLens,
    lenses,
    loadedGroup,
    loadedClassification,
    evidenceThemes: groupedEvidenceThemes,
    groups: documentClassifications,
    fieldInfoArray,
  }
}

async function action({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project, lensCode } = Object.fromEntries(formData) as Record<string, string>

  let errorMessage: string | undefined

  if (organisation == null || organisation === "") throw new Error("Missing organisation")
  if (project == null || project === "") throw new Error("Missing project")
  if (lensCode == null || lensCode === "") throw new Error("Missing lensCode")

  const classification = ((formData.get("classification") ?? "") as string).trim()
  if (classification === "") errorMessage = "Classification is required"

  switch (action) {
    case "CREATE": {
      if (errorMessage != null) break
      const theme = ((formData.get("theme") ?? "") as string).trim()

      try {
        await fetchAPI<Advisories>(
          `/api/UnityViewer/DocumentClassificationEvidenceTheme/all/${organisation}/${project}/${lensCode}`,
          {
            method: "POST",
            body: {
              classification,
              themes: theme === "" ? [] : formData.getAll("usage").map((usage) => ({ name: theme, usage })),
            },
          },
        )
          .then(handleResponse)
          .then((advisory) => {
            if (advisory.advisories.length > 0) {
              errorMessage = advisory.advisories.join(", ")
            } else {
              errorMessage = undefined
            }
          })
      } catch (error) {
        console.error(error)
        if ((error as { status: number }).status === 304) {
          errorMessage = "That mapping already exists, did you mean to edit it?"
        } else {
          errorMessage = "Error creating mapping"
        }
      }

      return { action, errorMessage }
    }
    case "DELETE_ALL": {
      if (errorMessage != null) break

      try {
        await fetchAPI(
          `/api/UnityViewer/DocumentClassificationEvidenceTheme/DeleteAll/${organisation}/${project}/${lensCode}`,
          { method: "POST", body: { classification } },
        ).then(throwIfNotOk)

        return { action, key: Math.random().toString(36) }
      } catch (error) {
        console.error(error)
        return { action, errorMessage: "Could not delete mapping" }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

async function mainAction({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project, lensCode } = Object.fromEntries(formData) as Record<string, string>

  if (organisation == null || organisation === "") throw new Error("Missing organisation")
  if (project == null || project === "") throw new Error("Missing project")
  if (lensCode == null || lensCode === "") throw new Error("Missing lensCode")

  switch (action) {
    case "DELETE_MAPPING": {
      try {
        const classification = formData.get("classification") as string
        const themeName = formData.get("evidenceTheme") as string
        const usage = formData.get("usage") as string

        await fetchAPI(
          `/api/UnityViewer/DocumentClassificationEvidenceTheme/delete/${organisation}/${project}/${lensCode}`,
          { method: "POST", body: { classification, theme: { name: themeName, usage: usage } } },
        ).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        return { action, errorMessage: "Couldn't delete theme mapping" }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action }
}

export function BrandDocumentClassificationEvidenceThemes() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((data, reason) => {
    if (reason.type !== "field") return

    if (reason.name === "organisation") data.delete("project")
    if (reason.name === "organisation" || reason.name === "project") {
      data.delete("lens")
      data.delete("group")
      data.delete("classification")
    }
    if (reason.name === "lens") {
      data.delete("group")
      data.delete("classification")
    }
    if (reason.name === "group") {
      data.delete("classification")
    }
  }, [])

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex flex-wrap items-center gap-4">
          <FiltersWrapper submitOnChange sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            {rootData.organisations.length > 1 && (
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
            )}
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
            {data.lenses.length > 1 && (
              <FiltersWrapper.Select
                name="lens"
                label="Lens"
                placeholder="Select a Lens"
                defaultValue={data.loadedLens}
                items={data.lenses.map((lens) => ({ value: lens.code, label: lens.name }))}
              />
            )}
            {data.groups.length > 1 && (
              <FiltersWrapper.Select
                name="group"
                label="Group"
                items={data.groups.map((group) => group.name)}
                placeholder="Select a Group"
                defaultValue={data.loadedGroup}
              />
            )}
            <FiltersWrapper.Select
              name="classification"
              label="Classification"
              items={
                data.loadedGroup != null
                  ? (data.groups
                      .find((group) => group.name === data.loadedGroup)
                      ?.subClassifications.map((sub) => sub.name) ?? [])
                  : []
              }
              placeholder={data.loadedGroup != null ? "Select a Classification" : "Select Group first"}
              defaultValue={data.loadedClassification}
            />
          </FiltersWrapper>
          <div className="flex-1" />
          <div className="-mt-2 flex items-center gap-2">
            <DocumentClassificationEvidenceThemesAddNewMapping />
            {data.loadedClassification != null &&
              data.loadedLens != null &&
              data.classificationToThemeMapping.length > 0 && <DocumentClassificationEvidenceThemesDeleteAll />}
          </div>
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  const flattenedMapping = useMemo(
    () =>
      data.classificationToThemeMapping.flatMap(({ classification, lens, themes }) =>
        themes.map((theme) => ({ lens, classification, theme, id: [classification, lens.code, theme.name].join("-") })),
      ),
    [data.classificationToThemeMapping],
  )

  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">Group</TableHead>
            <TableHead className="text-left">Classification</TableHead>
            <TableHead className="text-left">Lens</TableHead>
            <TableHead className="text-left">Theme</TableHead>
            <TableHead className="text-left">Usage</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {flattenedMapping.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="py-8 text-center text-muted-foreground">
                {(data.loadedLens ?? data.loadedGroup ?? data.loadedClassification) != null
                  ? "No evidence themes found for the selected filters."
                  : "No evidence themes found."}
              </TableCell>
            </TableRow>
          ) : (
            flattenedMapping.map((item) => {
              // Find the group for this classification
              const group =
                data.groups.find((group) => group.subClassifications.some((sub) => sub.name === item.classification))
                  ?.name ?? "Unknown"

              return (
                <TableRow key={item.id}>
                  <TableCell className="w-80">{group}</TableCell>
                  <TableCell className="w-80">{item.classification}</TableCell>
                  <TableCell className="w-80">{item.lens.name}</TableCell>
                  <TableCell className="w-80">{item.theme.name}</TableCell>
                  <TableCell className="w-80">
                    <ThemeUsageLabel usage={item.theme.usage} />
                  </TableCell>
                  <TableCell className="w-80">
                    <DocumentClassificationEvidenceThemeDeleteItem
                      theme={item.theme}
                      lens={item.lens.code}
                      classification={item.classification}
                    />
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function DocumentClassificationEvidenceThemeDeleteItem(props: DocumentClassificationEvidenceThemeDeleteItemProps) {
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost">
              <Trash2Icon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Mapping</TooltipContent>
      </Tooltip>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">Are you sure you want to delete this mapping?</p>
        <DocumentClassificationEvidenceThemeDeleteItemForm {...props} />
      </PopoverContent>
    </Popover>
  )
}

function DocumentClassificationEvidenceThemeDeleteItemForm({
  classification,
  theme,
  lens,
}: DocumentClassificationEvidenceThemeDeleteItemProps) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE_MAPPING" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="lensCode" value={lens} />
      <input type="hidden" name="classification" value={classification} />
      <input type="hidden" name="evidenceTheme" value={theme.name} />
      <input type="hidden" name="usage" value={theme.usage} />
      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        fetcher.data?.errorMessage !== undefined &&
        fetcher.data.errorMessage.length > 0 && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm Delete
      </Button>
    </fetcher.Form>
  )
}

function DocumentClassificationEvidenceThemesAddNewMapping() {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [open, setOpen] = useState(false)

  const closeDialog = useCallback(() => setOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 px-4 py-2">
          <PlusIcon className="h-4 w-4" />
          Add New Mapping
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Mapping</DialogTitle>
        </DialogHeader>
        <DocumentClassificationEvidenceThemesAddNewForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function DocumentClassificationEvidenceThemesAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  const [createAnother, setCreateAnother] = useState<boolean | "indeterminate">(false)
  const [formKey, setFormKey] = useState<string>()
  const [selectedGroup, setSelectedGroup] = useState<string>("")
  const [selectedClassification, setSelectedClassification] = useState<string>("")
  const [emptyMappingState, setEmptyMappingState] = useState<{
    showWarning: boolean
    confirmed: boolean
  }>({ showWarning: false, confirmed: false })
  const [selectedTheme, setSelectedTheme] = useState<string>("")

  const groupsWithClassifications = useMemo(() => {
    if (data?.groups == null) return {}

    return data.groups.reduce(
      (acc, group) => {
        acc[group.name] = group.subClassifications.map((sub) => sub.name)
        return acc
      },
      {} as Record<string, string[]>,
    )
  }, [data?.groups])

  const availableGroups = useMemo(() => {
    if (data?.groups == null) return []
    return data.groups.map((group) => group.name)
  }, [data?.groups])

  const availableClassifications = useMemo(() => {
    if (selectedGroup.length === 0) return []
    const classifications = groupsWithClassifications[selectedGroup]
    return classifications ?? []
  }, [selectedGroup, groupsWithClassifications])

  const evidenceThemeNames = useMemo(() => {
    return (
      data?.evidenceThemes
        .keys()
        .filter((k) => (data?.evidenceThemes?.get(k)?.usage?.length ?? 0) > 0)
        .toArray() ?? []
    )
  }, [data?.evidenceThemes])

  const themeUsages = useMemo(() => {
    if (selectedTheme.length > 0) {
      const themes = new Set(data?.evidenceThemes.get(selectedTheme)?.usage?.map((usage) => usage.toLowerCase()) ?? [])
      return themes.size === 0 ? UsageOptions : UsageOptions.filter((option) => themes.has(option.value.toLowerCase()))
    }
    return UsageOptions
  }, [data?.evidenceThemes, selectedTheme])

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.action !== "CREATE" || fetcher.data?.errorMessage != null) return

    if (createAnother === true) {
      setFormKey(Math.random().toString(36))
      setSelectedGroup("")
      setSelectedClassification("")
      setEmptyMappingState({ showWarning: false, confirmed: false })
    } else {
      closeDialog()
    }
  }, [closeDialog, createAnother, fetcher.data?.action, fetcher.data?.errorMessage, fetcher.state])

  const handleGroupChange = useCallback((value: string) => {
    setSelectedGroup(value)
    setSelectedClassification("")
  }, [])

  const handleClassificationChange = useCallback((value: string) => {
    setSelectedClassification(value)
  }, [])

  const handleThemeChange = useCallback((value: string) => {
    setSelectedTheme(value)
  }, [])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const theme = formData.get("theme") as string
    const usages = formData.getAll("usage") as string[]

    const isEmptyMapping = theme == null || theme === "" || usages.length === 0

    if (isEmptyMapping && !emptyMappingState.confirmed) {
      setEmptyMappingState({ showWarning: true, confirmed: false })
      return
    }

    void fetcher.submit(e.currentTarget, { method: "POST" })
  }

  if (data == null) return null

  return (
    <fetcher.Form onSubmit={handleSubmit} className="space-y-4">
      <input type="hidden" name="action" value="CREATE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="mb-2 block text-sm font-medium">Lens</label>
          <FieldInfoComponent
            name={BRAND_DOCUMENT_CLASSIFICATION_EVIDENCE_THEME_FIELDS.LENS}
            fieldDetails={data?.fieldInfoArray ?? []}
          />
        </div>
        <Select key={formKey} required name="lensCode" defaultValue={data.loadedLens}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Lens" />
          </SelectTrigger>
          <SelectContent>
            {data.lenses.map((lens) => (
              <SelectItem key={lens.code} value={lens.code}>
                {lens.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Group</label>
        <Select key={`group-${formKey}`} required name="group" value={selectedGroup} onValueChange={handleGroupChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Group" />
          </SelectTrigger>
          <SelectContent>
            {availableGroups.map((group) => (
              <SelectItem key={group} value={group}>
                {group}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="mb-2 block text-sm font-medium">Classification</label>
          <FieldInfoComponent
            name={BRAND_DOCUMENT_CLASSIFICATION_EVIDENCE_THEME_FIELDS.CLASSIFICATION}
            fieldDetails={data?.fieldInfoArray ?? []}
          />
        </div>
        <Select
          key={`classification-${formKey}-${selectedGroup}`}
          required
          name="classification"
          value={selectedClassification}
          onValueChange={handleClassificationChange}
          disabled={selectedGroup.length === 0}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={selectedGroup.length > 0 ? "Select Classification" : "Select Group first"} />
          </SelectTrigger>
          <SelectContent>
            {availableClassifications.map((classification) => (
              <SelectItem key={classification} value={classification}>
                {classification}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="mb-2 block text-sm font-medium">Theme</label>
          <FieldInfoComponent
            name={BRAND_DOCUMENT_CLASSIFICATION_EVIDENCE_THEME_FIELDS.THEME}
            fieldDetails={data?.fieldInfoArray ?? []}
          />
        </div>
        <Select key={`theme-${formKey}`} name="theme" value={selectedTheme} onValueChange={handleThemeChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Theme (Optional)" />
          </SelectTrigger>
          <SelectContent>
            {evidenceThemeNames.length > 0 &&
              evidenceThemeNames.map((theme) => (
                <SelectItem key={theme} value={theme}>
                  {theme}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor="theme-usage" className="mb-2 block text-sm font-medium">
            Usage
          </label>
          <FieldInfoComponent
            name={BRAND_DOCUMENT_CLASSIFICATION_EVIDENCE_THEME_FIELDS.USAGE}
            fieldDetails={data?.fieldInfoArray ?? []}
          />
        </div>
        <select
          key={`usage-${formKey}`}
          multiple
          name="usage"
          id="theme-usage"
          className="min-h-[80px] w-full rounded-md border p-2"
          size={3}
        >
          {themeUsages.map((option) => (
            <option key={option.value} value={option.value} className="text-sm">
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {emptyMappingState.showWarning && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <p className="mb-3 text-sm font-medium text-amber-800">
            ⚠️ Warning: When using an empty mapping set, this will disable all evidence themes in this classification.
            Are you sure this is desired?
          </p>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirm-empty-mapping"
              checked={emptyMappingState.confirmed}
              onCheckedChange={(checked) => setEmptyMappingState((prev) => ({ ...prev, confirmed: checked === true }))}
            />
            <label htmlFor="confirm-empty-mapping" className="text-sm font-medium text-amber-800">
              Yes, I want to create an empty mapping
            </label>
          </div>
        </div>
      )}
      <div className="flex items-center space-x-2 border-t pt-2">
        <Checkbox
          id="create-another"
          checked={createAnother}
          onCheckedChange={setCreateAnother}
          className="rounded border border-input"
        />
        <label htmlFor="create-another" className="text-sm leading-none font-medium">
          Create another mapping?
        </label>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          fetcher.data?.errorMessage !== undefined &&
          fetcher.data.errorMessage.length > 0 && (
            <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
          )
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={emptyMappingState.showWarning && !emptyMappingState.confirmed}>
          Create Mapping
        </Button>
      </div>
    </fetcher.Form>
  )
}

function DocumentClassificationEvidenceThemesDeleteAll() {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("deleteAccess")) return null

  return (
    <Popover key={fetcher.data?.key}>
      <PopoverTrigger asChild>
        <Button variant="ghost">
          <TrashIcon /> Delete All
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">
          Are you sure you want to <span className="font-semibold">DELETE ALL</span> mappings for the &quot;
          <span className="font-semibold">{data.loadedClassification}</span>&quot; Classification?
        </p>
        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
          <input type="hidden" name="action" value="DELETE_ALL" />
          <input type="hidden" name="project" value={data.loadedProject} />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          <input type="hidden" name="lensCode" value={data.loadedLens} />
          <input type="hidden" name="classification" value={data.loadedClassification} />
          {fetcher.state !== "idle" ? (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          ) : (
            fetcher.data?.errorMessage !== undefined &&
            fetcher.data.errorMessage.length > 0 && (
              <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
            )
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button>Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="ghost">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

BrandDocumentClassificationEvidenceThemes.loader = loader
BrandDocumentClassificationEvidenceThemes.action = action
BrandDocumentClassificationEvidenceThemes.Main = Main
BrandDocumentClassificationEvidenceThemes.routeId = routeId

Main.action = mainAction

interface DocumentClassificationEvidenceThemeDeleteItemProps {
  lens: string
  theme: DocumentTheme
  classification: string
}
